import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getAnalyticsSnapshots, getModels, recomputeAnalytics } from '../api'
import { getRealmFromPath } from '../lib/realm'
import { Select, PageSection, FilterGroup, FiltersRow } from '../components/ui'
import StructuredDataView from '../components/StructuredDataView'
import { useI18n } from '../i18n'
import '../styles/pages/models.css'
import '../styles/pages/page-common.css'
import '../styles/pages/analytics.css'

const SCOPE_OPTIONS = [
  { value: '', labelKey: 'analytics.scope.all' },
  { value: 'global', labelKey: 'analytics.scope.global' },
  { value: 'model', labelKey: 'analytics.scope.model' },
  { value: 'fan_model', labelKey: 'analytics.scope.fanModel' },
]

const SNAPSHOT_OPTIONS = [
  { value: '', labelKey: 'analytics.snapshot.all' },
  { value: 'fan_states', labelKey: 'analytics.snapshot.fanStates' },
  { value: 'revenue_summary', labelKey: 'analytics.snapshot.revenue' },
  { value: 'bot_performance', labelKey: 'analytics.snapshot.performance' },
  { value: 'openai_cost', labelKey: 'analytics.snapshot.openaiCost' },
  { value: 'fan_profile', labelKey: 'analytics.snapshot.fanProfile' },
]

function collectNumericEntries(data) {
  if (!data || typeof data !== 'object') return []
  return Object.entries(data).filter(([, v]) => typeof v === 'number').slice(0, 4)
}

export default function AnalyticsPage() {
  const { t, lang } = useI18n()
  const { pathname } = useLocation()
  const realm = getRealmFromPath(pathname)
  const canFilterTestReal = realm === 'admin'

  const [scopeType, setScopeType] = useState('')
  const [snapshotType, setSnapshotType] = useState('')
  const [modelId, setModelId] = useState('')
  const [fanModelScopeId, setFanModelScopeId] = useState('')
  const [isTestFilter, setIsTestFilter] = useState(null)
  const [models, setModels] = useState([])
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [recomputing, setRecomputing] = useState(false)

  useEffect(() => {
    getModels()
      .then((r) => {
        const list = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []
        setModels(list)
      })
      .catch(() => setModels([]))
  }, [])

  const resolvedScopeId = useMemo(() => {
    if (scopeType === 'model') return modelId || ''
    if (scopeType === 'fan_model') return fanModelScopeId.trim()
    return ''
  }, [scopeType, modelId, fanModelScopeId])

  const loadSnapshots = async () => {
    setLoading(true)
    try {
      const r = await getAnalyticsSnapshots({
        scopeType: scopeType || undefined,
        scopeId: resolvedScopeId || undefined,
        snapshotType: snapshotType || undefined,
        isTest: canFilterTestReal ? isTestFilter ?? undefined : undefined,
        limit: 300,
      })
      setItems(Array.isArray(r?.data) ? r.data : [])
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSnapshots()
  }, [scopeType, snapshotType, resolvedScopeId, canFilterTestReal, isTestFilter])

  const latestCards = useMemo(() => {
    const map = new Map()
    for (const item of items) {
      const key = `${item.scope_type}|${item.scope_id || '-'}|${item.snapshot_type}|${item.period || '-'}`
      if (!map.has(key)) map.set(key, item)
    }
    return Array.from(map.values()).slice(0, 8)
  }, [items])

  const modelOptions = [
    { value: '', label: t('analytics.model.all') },
    ...models.map((m) => ({ value: String(m.id), label: m.of_email || m.name || `#${m.id}` })),
  ]
  const scopeOptions = SCOPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))
  const snapshotOptions = SNAPSHOT_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))

  const handleRecompute = async () => {
    setRecomputing(true)
    try {
      await recomputeAnalytics({ sync: false })
      await loadSnapshots()
    } finally {
      setRecomputing(false)
    }
  }

  return (
    <PageSection
      className="analytics-page"
      title={t('analytics.title')}
    >
      <FiltersRow
        actions={
          <button
            type="button"
            className="analytics-refresh-btn"
            onClick={handleRecompute}
            disabled={recomputing}
          >
            {recomputing ? t('analytics.recompute.running') : t('analytics.recompute.action')}
          </button>
        }
      >
        <FilterGroup label={t('analytics.scope.label')}>
          <Select
            className="filter-select"
            value={scopeType}
            options={scopeOptions}
            onChange={(v) => {
              setScopeType(v || '')
              setModelId('')
              setFanModelScopeId('')
            }}
            placeholder={t('analytics.scope.label')}
            dropdownPosition="down"
          />
        </FilterGroup>
        <FilterGroup label={t('analytics.snapshot.label')}>
          <Select
            className="filter-select"
            value={snapshotType}
            options={snapshotOptions}
            onChange={(v) => setSnapshotType(v || '')}
            placeholder={t('analytics.snapshot.label')}
            dropdownPosition="down"
          />
        </FilterGroup>
        {scopeType === 'model' && (
          <FilterGroup label={t('analytics.model.label')}>
            <Select
              className="filter-select"
              value={modelId}
              options={modelOptions}
              onChange={(v) => setModelId(v || '')}
              placeholder={t('analytics.model.label')}
              dropdownPosition="down"
            />
          </FilterGroup>
        )}
        {scopeType === 'fan_model' && (
          <FilterGroup label={t('analytics.scope.fanModel')}>
            <input
              className="filter-input"
              value={fanModelScopeId}
              onChange={(e) => setFanModelScopeId(e.target.value)}
              placeholder={t('analytics.fanModel.placeholder')}
            />
          </FilterGroup>
        )}
        {canFilterTestReal && (
          <FilterGroup label={t('chats.filter.type')}>
            <Select
              className="filter-select"
              value={isTestFilter === null ? '' : String(isTestFilter)}
              onChange={(v) => setIsTestFilter(v === '' ? null : v === 'true')}
              options={[
                { value: '', label: t('modelData.allTypes') },
                { value: 'false', label: t('chats.tab.real') },
                { value: 'true', label: t('chats.tab.test') },
              ]}
              placeholder={t('modelData.allTypes')}
              dropdownPosition="down"
            />
          </FilterGroup>
        )}
      </FiltersRow>

      {loading ? (
        <p className="analytics-loading">{t('analytics.loading')}</p>
      ) : (
        <>
          <div className="analytics-cards">
            {latestCards.map((item) => (
              <article key={item.id} className="analytics-card">
                <div className="analytics-card-head">
                  <strong>{item.snapshot_type}</strong>
                  <span>{item.period || 'now'}</span>
                </div>
                <div className="analytics-card-scope">
                  {item.scope_type}:{item.scope_id || '-'}
                </div>
                <div className="analytics-card-metrics">
                  {collectNumericEntries(item.data).map(([k, v]) => (
                    <div key={k} className="analytics-metric">
                      <span>{k}</span>
                      <b>{Number(v).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US')}</b>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('analytics.table.computedAt')}</th>
                  <th>{t('analytics.table.scope')}</th>
                  <th>{t('analytics.table.type')}</th>
                  <th>{t('analytics.table.period')}</th>
                  <th>{t('analytics.table.data')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.computed_at ? new Date(item.computed_at).toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US') : '—'}</td>
                    <td>{item.scope_type}:{item.scope_id || '-'}</td>
                    <td>{item.snapshot_type}</td>
                    <td>{item.period || '—'}</td>
                    <td>
                      <details>
                        <summary>{t('analytics.table.open')}</summary>
                        <StructuredDataView value={item.data} compact className="analytics-structured-data" />
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && <p className="analytics-empty">{t('analytics.empty')}</p>}
          </div>
        </>
      )}
    </PageSection>
  )
}
